import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { api, errorMessage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { ScrollArea } from "../../components/ui/scroll-area";
import Logo from "../../components/Logo";
import { LogOut, FileSignature } from "lucide-react";
import { toast } from "sonner";

const MSA_BODY_EN = `MARKETING SERVICES AGREEMENT

This Marketing Services Agreement (the "Agreement") is entered into as of the date of electronic signature between PMB Loans ("Company") and the undersigned broker ("Marketing Partner").

1. SCOPE OF SERVICES
   1.1 Marketing Partner will promote Company's private-lending programs using only Company-approved marketing materials and referral links provided through the PMB Loans broker portal.
   1.2 Marketing Partner is an independent contractor and is NOT authorized to originate, negotiate, structure, price, or advise on any loan, nor to quote rates or terms.

2. COMPLIANCE
   2.1 Marketing Partner shall include the required disclosure in all marketing communications: "I am a marketing partner of PMB Loans. I do not originate, negotiate, or advise on loans. All lending activities are handled by PMB Loans' licensed team."
   2.2 Marketing Partner shall comply with all applicable federal, state, and local laws, including the SAFE Act, TILA, RESPA, CAN-SPAM, and TCPA.

3. REFERRALS
   3.1 Company will attribute borrower applications submitted through Marketing Partner's referral link within a 30-day attribution window.
   3.2 Marketing Partner agrees not to accept applications, documents, or funds directly from borrowers.

4. CONFIDENTIALITY
   4.1 Marketing Partner will keep confidential all non-public information received from Company, including borrower data, lead lists, rate sheets, and playbooks.

5. TERM & TERMINATION
   5.1 Either party may terminate this Agreement at any time with 7 days' written notice.
   5.2 Upon termination, Marketing Partner shall cease using Company's marketing materials and shall return or destroy all confidential information.

6. NO LOAN ORIGINATION
   6.1 Marketing Partner acknowledges that engaging in activities that constitute loan origination under applicable law (without proper licensure) is a material breach of this Agreement and grounds for immediate termination.

7. GOVERNING LAW
   This Agreement is governed by the laws of the United States and the state in which Company's principal office is located, without regard to conflict-of-laws principles.

By entering your full legal name below and clicking "Sign & Continue", you acknowledge (a) you have read this Agreement, (b) you agree to be bound by its terms, (c) your electronic signature has the same legal effect as a handwritten signature under applicable e-signature law, and (d) your IP address, user agent, and timestamp will be recorded for audit purposes.`;

const MSA_BODY_ES = `ACUERDO DE SERVICIOS DE MARKETING

Este Acuerdo se celebra entre PMB Loans ("Compañía") y el broker firmante ("Socio de Marketing") a la fecha de la firma electrónica.

1. ALCANCE
   1.1 El Socio promoverá los programas de préstamo privado de la Compañía utilizando únicamente materiales y enlaces aprobados a través del portal.
   1.2 El Socio es un contratista independiente y NO está autorizado para originar, negociar, estructurar, cotizar o asesorar sobre préstamos.

2. CUMPLIMIENTO
   2.1 El Socio incluirá la divulgación requerida en todas las comunicaciones: "Soy un socio de marketing de PMB Loans. No origino, negocio ni asesoro préstamos. Todas las actividades de préstamo son gestionadas por el equipo licenciado de PMB Loans."
   2.2 El Socio cumplirá con todas las leyes aplicables (SAFE Act, TILA, RESPA, CAN-SPAM, TCPA).

3. REFERENCIAS
   3.1 La Compañía atribuirá las solicitudes enviadas mediante el enlace del Socio dentro de una ventana de 30 días.
   3.2 El Socio no aceptará solicitudes, documentos ni fondos directamente de prestatarios.

4. CONFIDENCIALIDAD
   4.1 El Socio mantendrá confidencial toda información no pública recibida (datos de prestatarios, listas de leads, tarifas).

5. VIGENCIA Y TERMINACIÓN
   5.1 Cualquiera de las partes puede terminar con aviso de 7 días.
   5.2 Al terminar, el Socio dejará de usar los materiales y devolverá/destruirá la información confidencial.

6. SIN ORIGINACIÓN DE PRÉSTAMOS
   6.1 Participar en actividades de originación sin licencia es un incumplimiento material.

7. LEY APLICABLE
   Este Acuerdo se rige por las leyes de los Estados Unidos y el estado de la oficina principal de la Compañía.

Al ingresar tu nombre legal completo y hacer clic en "Firmar y Continuar" reconoces (a) haber leído este Acuerdo, (b) aceptar estar obligado por sus términos, (c) que tu firma electrónica tiene el mismo efecto legal que una firma manuscrita bajo la ley aplicable, y (d) que tu dirección IP, user agent y fecha/hora quedarán registrados para auditoría.`;

export default function SponsorMsa() {
  const { user, refresh, logout } = useAuth();
  const { t, lang } = useT();
  const nav = useNavigate();
  const [signerName, setSignerName] = useState(user?.name || "");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.msa_signed) nav("/broker", { replace: true });
  }, [user, nav]);

  const sign = async (e) => {
    e.preventDefault();
    if (!signerName.trim()) { toast.error("Name required"); return; }
    if (!agree) { toast.error("You must agree to continue"); return; }
    setBusy(true);
    try {
      await api.post("/msa/sign", { signer_name: signerName.trim(), agree: true });
      await refresh();
      toast.success("MSA signed");
      nav("/broker", { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => { await logout(); nav("/login"); };
  const body = lang === "es" ? MSA_BODY_ES : MSA_BODY_EN;

  return (
    <div className="portal-theme min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <Logo height={56} />
          </div>
          <button onClick={doLogout} className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-rose-600" data-testid="msa-logout-btn">
            <LogOut className="h-3 w-3" /> {t("common.signOut")}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10 animate-fade-in-up">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 rounded-sm border border-blue-200 bg-blue-50 px-3 py-1 text-[11px] uppercase tracking-widest text-blue-700">
            <FileSignature className="h-3 w-3" /> {t("msa.title")}
          </div>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-900">{t("msa.title")}</h1>
          <p className="mt-2 text-sm text-slate-500">{t("msa.subtitle")}</p>
        </div>

        <Card className="rounded-sm border-slate-200 bg-white p-0 shadow-none" data-testid="msa-body-card">
          <ScrollArea className="h-[400px]">
            <pre className="whitespace-pre-wrap p-6 font-sans text-[13px] leading-relaxed text-slate-700">{body}</pre>
          </ScrollArea>
        </Card>

        <form onSubmit={sign} className="mt-6 space-y-4" data-testid="msa-form">
          <div>
            <Label className="text-slate-700">{t("msa.signerName")}</Label>
            <Input
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              className="mt-1 rounded-sm border-slate-300 bg-white text-slate-900 focus-visible:ring-blue-500/30"
              data-testid="msa-signer-input"
              required
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              checked={agree}
              onCheckedChange={(v) => setAgree(!!v)}
              className="mt-1 border-slate-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
              data-testid="msa-agree-checkbox"
            />
            <Label className="cursor-pointer text-sm text-slate-700" onClick={() => setAgree(!agree)}>
              {t("msa.agree")}
            </Label>
          </div>
          <Button
            type="submit"
            disabled={busy || !agree || !signerName.trim()}
            size="lg"
            className="w-full rounded-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            data-testid="msa-submit-btn"
          >
            {busy ? t("msa.signing") : t("msa.sign")}
          </Button>
        </form>
      </main>
    </div>
  );
}
